import { useEffect, useState } from 'react';
import api from '../../../lib/api';

/**
 * Önceki dönem (aynı uzunlukta, hemen önceki pencere) için /routes/summary
 * çağrısı yapar — RoutesSummary kartlarındaki trend okları için.
 *
 * Kurallar:
 *   • dateRange explicit ise (start..end) → aynı uzunlukta bir önceki window.
 *   • selectedMonths tek bir ay ise → bir önceki takvim ayı.
 *   • Çoklu ay veya hiç filtre yoksa → karşılaştırma kapalı (null döner).
 *   • Tek müşteri seçili olmadığında karşılaştırma kapalı (cross-customer
 *     toplamı anlamsız olurdu).
 */
function shiftYM(ym, deltaMonths) {
  const [y, m] = ym.split('-').map(Number);
  const total = y * 12 + (m - 1) + deltaMonths;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

function shiftIsoDate(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function diffDaysInclusive(startIso, endIso) {
  const a = new Date(startIso);
  const b = new Date(endIso);
  return Math.round((b - a) / 86400000) + 1;
}

export function usePreviousPeriodSummary({
  selectedCustomers,
  selectedFirm,
  selectedMonths,
  dateRange,
}) {
  const [data, setData] = useState(null); // { totals: {...}, label: 'önceki 7 gün' }
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      // Tek müşteri zorunlu
      const customers = Array.from(selectedCustomers || []);
      if (customers.length !== 1) {
        setData(null);
        return;
      }
      const customer = customers[0];

      // Önceki dönem hesabı
      let params = null;
      let label = '';
      if (dateRange?.start && dateRange?.end) {
        const len = diffDaysInclusive(dateRange.start, dateRange.end);
        const prevEnd = shiftIsoDate(dateRange.start, -1);
        const prevStart = shiftIsoDate(prevEnd, -(len - 1));
        params = { start_date: prevStart, end_date: prevEnd };
        label = `önceki ${len} gün`;
      } else if ((selectedMonths || []).length === 1) {
        const m = selectedMonths[0];
        params = { month: shiftYM(m, -1) };
        label = 'önceki ay';
      } else {
        setData(null);
        return;
      }

      setLoading(true);
      try {
        const qs = new URLSearchParams({ customer, ...params });
        if (selectedFirm) qs.append('firm_id', selectedFirm);
        const res = await api.get(`/routes/summary?${qs.toString()}`);
        if (cancelled) return;
        const totals = res.data?.data?.totals || null;
        setData(totals ? { totals, label } : null);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [selectedCustomers, selectedFirm, selectedMonths, dateRange]);

  return { previousSummary: data, loading };
}
